"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import {
  COMMENT_NAME_MAX,
  COMMENT_TEXT_MAX,
  DOCTOR_TOKEN_AVATAR,
  filterCommunityDisplayName,
  generateGuestDisplayName,
  publicNameError,
} from "@/lib/community/communityCommentsFilter";
import {
  getAccessTokenExpiresAtMs,
  getAuthToken,
  getCurrentUser,
  isAuthenticated,
} from "@/utils/authUtils";

const POLL_MS = 45_000;
const GUEST_NAME_KEY = "idx_community_guest_name";

function readStoredGuestName() {
  try {
    const stored = String(window.localStorage.getItem(GUEST_NAME_KEY) || "").trim();
    const checked = filterCommunityDisplayName(stored);
    return checked.ok ? checked.displayName : "";
  } catch {
    return "";
  }
}

function persistGuestName(name) {
  try {
    window.localStorage.setItem(GUEST_NAME_KEY, name);
  } catch {
    /* ignore quota / private mode */
  }
}

function hasActiveAccount() {
  try {
    if (!isAuthenticated()) return false;
    const token = getAuthToken();
    if (!token) return false;
    const exp = getAccessTokenExpiresAtMs(token);
    if (exp != null && exp < Date.now()) return false;
    return true;
  } catch {
    return false;
  }
}

function resolveComposerIdentity() {
  let avatarUrl = "";
  let loggedIn = false;

  try {
    if (hasActiveAccount()) {
      const data = getCurrentUser();
      const user = data?.user || data;
      const name = String(user?.username || user?.name || "").trim();
      avatarUrl = String(user?.avatar || "").trim();
      const checked = filterCommunityDisplayName(name);
      if (checked.ok) {
        return { loggedIn: true, name: checked.displayName, avatarUrl };
      }
      loggedIn = true;
    }
  } catch {
    /* fall through to guest name */
  }

  const stored = readStoredGuestName();
  const name = stored || generateGuestDisplayName();
  if (!stored) persistGuestName(name);
  return { loggedIn, name, avatarUrl };
}

function CommentAvatar({ src, size = 36, className = "" }) {
  const [failed, setFailed] = useState(false);
  const resolved = !failed && src ? src : DOCTOR_TOKEN_AVATAR;
  return (
    // eslint-disable-next-line @next/next/no-img-element -- profile or static sticker
    <img
      src={resolved}
      alt=""
      width={size}
      height={size}
      onError={() => setFailed(true)}
      className={className}
    />
  );
}

function formatCommentTime(iso) {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  const diff = Date.now() - ms;
  if (diff < 45_000) return "just now";
  if (diff < 90_000) return "1 min ago";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 5_400_000) return "1 hour ago";
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} hours ago`;
  if (diff < 172_800_000) return "yesterday";
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/**
 * Public comment thread. Login is optional; Doctor Token is the default avatar.
 * @param {{ mint?: string, page?: "community", embedded?: boolean }} props
 */
export function CommunitySignalComments({ mint, page, embedded = false }) {
  const mintTrim = useMemo(() => String(mint || "").trim(), [mint]);
  const pageTrim = useMemo(() => String(page || "").trim().toLowerCase(), [page]);
  const query = useMemo(() => {
    if (mintTrim) return `mint=${encodeURIComponent(mintTrim)}`;
    if (pageTrim === "community") return "page=community";
    return "";
  }, [mintTrim, pageTrim]);

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(Boolean(query));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [composerAvatar, setComposerAvatar] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameError, setNameError] = useState("");

  useLayoutEffect(() => {
    const identity = resolveComposerIdentity();
    setLoggedIn(identity.loggedIn);
    setDisplayName(identity.name);
    setComposerAvatar(identity.avatarUrl);
    setNameDraft(identity.name);
  }, []);

  const loadComments = useCallback(async () => {
    if (!query) return;
    try {
      const res = await fetch(`/api/community/comments?${query}`, { cache: "no-store" });
      const json = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(json.comments)) {
        setComments(json.comments);
      }
    } catch {
      /* keep previous list */
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (!query) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    loadComments();
    const id = window.setInterval(loadComments, POLL_MS);
    return () => window.clearInterval(id);
  }, [query, loadComments]);

  const applyNameDraft = useCallback(() => {
    const named = filterCommunityDisplayName(nameDraft);
    if (!named.ok) {
      setNameError(publicNameError(named.reason));
      return null;
    }
    setNameError("");
    setDisplayName(named.displayName);
    setNameDraft(named.displayName);
    setEditingName(false);
    if (!loggedIn) persistGuestName(named.displayName);
    return named.displayName;
  }, [loggedIn, nameDraft]);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!query || submitting) return;
      setError("");
      const resolvedName = editingName ? applyNameDraft() : displayName;
      if (!resolvedName) return;
      const named = filterCommunityDisplayName(resolvedName);
      if (!named.ok) {
        setNameError(publicNameError(named.reason));
        return;
      }
      setNameError("");
      setSubmitting(true);
      try {
        const res = await fetch("/api/community/comments", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...(mintTrim ? { mint: mintTrim } : { page: "community" }),
            text,
            displayName: named.displayName,
            avatarUrl: composerAvatar,
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(String(json.error || "Could not post comment."));
          return;
        }
        if (Array.isArray(json.comments)) setComments(json.comments);
        setText("");
        setDisplayName(named.displayName);
        if (!loggedIn) persistGuestName(named.displayName);
      } catch {
        setError("Could not post comment. Try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [applyNameDraft, composerAvatar, displayName, editingName, loggedIn, mintTrim, query, submitting, text],
  );

  if (!query) return null;

  return (
    <section
      className={
        embedded
          ? "scroll-mt-24"
          : "mt-14 max-w-3xl scroll-mt-24 border-t border-gray-700/70 pt-10"
      }
      aria-labelledby="community-comments-h2"
    >
      <h2 id="community-comments-h2" className="text-xl font-semibold text-white sm:text-2xl">
        Comments
      </h2>
      <p className="mt-2 text-sm text-gray-500">
        No account needed. Guests get a unique name you can edit. Logged-in comments use your
        profile photo; guests use the Doctor Token avatar. Abuse in comments or names is blocked.
      </p>

      <form className="mt-6 space-y-3" onSubmit={onSubmit}>
        <div className="flex items-start gap-3">
          <CommentAvatar
            src={composerAvatar}
            size={40}
            className="mt-1 h-10 w-10 shrink-0 rounded-full border border-gray-700/80 bg-gray-900 object-cover"
          />
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              {editingName ? (
                <div className="flex flex-wrap items-center gap-2">
                  <label className="min-w-0 flex-1">
                    <span className="sr-only">Display name</span>
                    <input
                      type="text"
                      value={nameDraft}
                      onChange={(e) => {
                        setNameDraft(e.target.value);
                        if (nameError) setNameError("");
                      }}
                      maxLength={COMMENT_NAME_MAX}
                      placeholder="Choose a name"
                      autoComplete="nickname"
                      autoFocus
                      className="w-full rounded-lg border border-gray-700 bg-gray-950/70 px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={applyNameDraft}
                    className="rounded-lg bg-sky-500 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-400"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNameDraft(displayName);
                      setNameError("");
                      setEditingName(false);
                    }}
                    className="rounded-lg border border-gray-700 px-3 py-2 text-sm font-medium text-gray-300 hover:border-gray-500 hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <p className="min-w-0 truncate text-sm font-semibold text-white">
                    {displayName || "Assigning name…"}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setNameDraft(displayName);
                      setNameError("");
                      setEditingName(true);
                    }}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-sky-400 hover:text-sky-300"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    Edit name
                  </button>
                </div>
              )}
              {nameError ? (
                <p className="mt-1.5 text-sm text-rose-400" role="alert">
                  {nameError}
                </p>
              ) : null}
            </div>
            <label className="block">
              <span className="sr-only">Comment</span>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={COMMENT_TEXT_MAX}
                rows={3}
                required
                placeholder="Share a thought about this page…"
                className="w-full resize-y rounded-lg border border-gray-700 bg-gray-950/70 px-3 py-2 text-sm leading-relaxed text-white placeholder:text-gray-600 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </label>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-gray-600">
                {text.length}/{COMMENT_TEXT_MAX}
              </p>
              <button
                type="submit"
                disabled={submitting || !text.trim()}
                className="inline-flex items-center justify-center rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? "Posting…" : "Post comment"}
              </button>
            </div>
            {error ? (
              <p className="text-sm text-rose-400" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </div>
      </form>

      <div className="mt-8 space-y-5">
        {loading ? (
          <p className="text-sm text-gray-500">Loading comments…</p>
        ) : comments.length === 0 ? (
          <p className="text-sm text-gray-500">Be the first to comment.</p>
        ) : (
          comments.map((item) => (
            <article key={item.id} className="flex items-start gap-3">
              <CommentAvatar
                src={item.avatarUrl}
                size={36}
                className="h-9 w-9 shrink-0 rounded-full border border-gray-700/80 bg-gray-900 object-cover"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <h3 className="text-sm font-semibold text-white">{item.displayName}</h3>
                  <time
                    className="text-xs text-gray-500"
                    dateTime={item.createdAt}
                    title={item.createdAt}
                  >
                    {formatCommentTime(item.createdAt)}
                  </time>
                </div>
                <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-300">
                  {item.text}
                </p>
              </div>
            </article>
          ))
        )}
      </div>
    </section>
  );
}
